/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';

const offlineClassroomScopes: string[] = [
  'https://www.googleapis.com/auth/classroom.courses.readonly',
  'https://www.googleapis.com/auth/classroom.rosters.readonly',
  'https://www.googleapis.com/auth/classroom.profile.emails',
  'https://www.googleapis.com/auth/classroom.push-notifications',
  'https://www.googleapis.com/auth/classroom.coursework.me',
  'https://www.googleapis.com/auth/classroom.coursework.students'
];

const listClassroomsScopes = [
  'https://www.googleapis.com/auth/classroom.courses.readonly'
];

type TokenClient = {
  requestAccessToken: () => void;
};

type TokenResponse = {
  accessToken: string;
  tokenType: string;
  scope: string;
  errorDescription: string;
};

type CodeClient = {
  requestCode: () => any;
};

type CodeResponse = {
  code: string;
  scope: string;
  errorDescription: string;
};

type SignInResponse = {
  idToken: string;
  selectBy: string;
};

type SignInResponseHandler = (response: SignInResponse) => void;

/**
 * The token client is used to retrieve the access token needed to pull
 * the list of google classrooms where the user is a teacher.
 *
 * This follows the `implicit` grant model: https://developers.google.com/identity/oauth2/web/guides/use-token-model
 *
 * @param scopes
 * @param callback
 * @returns
 */
const initTokenClient = (
  scopes: string[],
  callback: (response: TokenResponse) => void
): TokenClient => {
  const google = (window as any).google;

  const client = google.accounts.oauth2.initTokenClient({
    // eslint-disable-next-line @typescript-eslint/camelcase
    client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
    scope: scopes.join(' '),
    // eslint-disable-next-line @typescript-eslint/camelcase
    callback: (response: any) => {
      console.log('google tokenClient response', { response });

      callback({
        accessToken: response.access_token,
        tokenType: response.token_type,
        scope: response.scope,
        errorDescription: response.error_description
      });
    }
  });

  console.log('initialized token client', { client });

  return client as TokenClient;
};

/**
 * The code client is used to retrieve the authorization code used to
 * gain offline access to a user's classroom data.  We use this data to
 * sync rosters and sync assignments.
 *
 * The code client follows the authorization code model: https://developers.google.com/identity/oauth2/web/guides/use-code-model
 *
 * @param scopes
 * @param callback
 * @returns
 */
const initCodeClient = (
  scopes: string[],
  callback: (response: CodeResponse) => void
): CodeClient => {
  const google = (window as any).google;

  const client = google.accounts.oauth2.initCodeClient({
    // eslint-disable-next-line @typescript-eslint/camelcase
    client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
    scope: scopes.join(' '),
    // eslint-disable-next-line @typescript-eslint/camelcase
    ux_mode: 'popup',
    callback: (response: any) => {
      console.log('google codeClient response', { response });

      callback({
        code: response.code,
        scope: response.scope,
        errorDescription: response.error_description
      });
    }
  });

  console.log('initialized code client', { client });

  return client as CodeClient;
};

const requestCodeForScopes = (scopes: string[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    const client = initCodeClient(scopes, response => {
      if (response.errorDescription) {
        reject(new Error(response.errorDescription));
      }

      resolve(response.code);
    });

    client.requestCode();
  });
};

const requestTokenForScopes = (scopes: string[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    const client = initTokenClient(scopes, response => {
      if (response.errorDescription) {
        reject(new Error(response.errorDescription));
      }

      resolve(response.accessToken);
    });

    client.requestAccessToken();
  });
};

const requestListClassroomsToken = () => {
  return requestTokenForScopes(listClassroomsScopes);
};

const defaultNoopHandler = (response: SignInResponse) => {
  console.log('sign in response', { response });
};

let registeredSignInCallback: SignInResponseHandler = defaultNoopHandler;

/**
 * We're not supposed to repeatedly call `accounts.id.initialize`, which is where
 * we set the login callback, so instead we use this janky workaround.
 *
 * @param callback
 */
const registerSignInCallback = (
  callback: (response: SignInResponse) => void
) => {
  registeredSignInCallback = callback;
};

const internalSignInCallback = (response: SignInResponse) => {
  console.log('internal callback', { response });

  registeredSignInCallback(response);
};

let idInitialized = false;

const initIdentityService = () => {
  const google = (window as any).google;

  if (idInitialized) {
    return { google };
  }

  const config = {
    // eslint-disable-next-line @typescript-eslint/camelcase
    client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
    // eslint-disable-next-line @typescript-eslint/camelcase
    auto_select: true,

    callback: (response: any) => {
      const transformed = {
        idToken: response.credential,
        selectBy: response.select_by
      };
      internalSignInCallback(transformed);
    }
  };

  google.accounts.id.initialize(config);

  idInitialized = true;

  return { google };
};

const signOut = async (): Promise<void> => {
  const { google } = initIdentityService();

  console.log('signing out');

  google.accounts.id.disableAutoSelect();
};

export interface GoogleClassroomCourse {
  id: string;
  name: string;
  section: string;
  descriptionHeading: string;
  room: string;
  courseState: string;
}

/**
 * Sample Response
 *
 * [
 *  {
 *    "id": "114692011160",
 *    "name": "Let us learn",
 *    "section": "12",
 *    "descriptionHeading": "Let us learn 12",
 *    "room": "412A",
 *    "ownerId": "111483931785408535254",
 *    "creationTime": "2020-06-22T20:08:41.344Z",
 *    "updateTime": "2020-06-22T20:08:40.449Z",
 *    "enrollmentCode": "ggzkqjx",
 *    "courseState": "ACTIVE",
 *    "alternateLink": "https://classroom.google.com/c/MTE0NjkyMDExMTYw",
 *    "teacherGroupEmail": "Let_us_learn_12_teachers_d240a312@unrulr.com",
 *    "courseGroupEmail": "Let_us_learn_12_6dea1d24@unrulr.com",
 *    "teacherFolder": {
 *      "id": "0B0TPm5FP3LTafkZYdHg3andCMmVkc1FRLW9iZWVibWNtTjZoaFVPZUdNbUkySXRJSmtfZnM",
 *      "title": "Let us learn 12",
 *      "alternateLink": "https://drive.google.com/drive/folders/0B0TPm5FP3LTafkZYdHg3andCMmVkc1FRLW9iZWVibWNtTjZoaFVPZUdNbUkySXRJSmtfZnM"
 *    },
 *    "guardiansEnabled": false,
 *    "calendarId": "unrulr.com_classroom8a4ed325@group.calendar.google.com"
 *  }
 * ]
 **/

const getClassrooms = async (): Promise<GoogleClassroomCourse[]> => {
  const listClassroomsCode = await requestListClassroomsToken();

  console.log('accessCode', { listClassroomsCode });

  const result = await axios.get(
    `https://classroom.googleapis.com/v1/courses?key=${process.env.VUE_APP_GOOGLE_API_KEY}&teacherId=me`,
    {
      headers: {
        Authorization: `Bearer ${listClassroomsCode}`
      }
    }
  );

  console.log('classrooms result', { result });

  // If the user has no classrooms, `data.courses` is undefined
  return (result.data.courses as GoogleClassroomCourse[]) || [];
};

const grantOfflineAccess = async (): Promise<string> => {
  return requestCodeForScopes(offlineClassroomScopes);
};

enum SignInText {
  signInWith,
  signUpWith
}

const getButtonTextParameter = (type: SignInText): string => {
  switch (type) {
    case SignInText.signInWith:
      return 'signin_with';
    case SignInText.signUpWith:
      return 'signup_with';
  }
};

const renderSignInButton = async (
  reference: any,
  signInText: SignInText,
  callback: SignInResponseHandler
) => {
  const { google } = initIdentityService();

  registerSignInCallback(callback);

  google.accounts.id.renderButton(reference, {
    text: getButtonTextParameter(signInText)
  });
};

export {
  getClassrooms,
  grantOfflineAccess,
  SignInResponse,
  SignInText,
  renderSignInButton,
  signOut
};
